import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../config/firebase.js'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
import LoadingSpinner from '../components/LoadingSpinner.jsx'
import DashboardPage from '@express/components/pages/DashboardPage.jsx'
import { usePollingController } from '@express/hooks/usePollingController.js'
import PresenceConfirmation from '@express/components/dashboard/PresenceConfirmation.jsx'

/**
 * Cloud Dashboard page - React 19+ optimized Firebase Functions API proxy with intelligent caching
 * 
 * Architecture Pattern: Same as Analysis.jsx but with performance optimizations
 * - memo component with Firebase Functions
 * - fetch interceptor for API routing with intelligent caching and throttling
 * - direct DashboardPage.jsx rendering (no wrapper)
 * - zero express modifications required
 * 
 * Performance Optimizations:
 * - Real-time data: Cached 30s (reduces cloud calls from 60/min to 2/min)
 * - Dashboard fields: Cached 30s (rarely change)
 * - Faults: Cached 30s (balance between freshness and efficiency) 
 * - Historical data: Cached 60s per device/measurement pair
 */
const Dashboard = memo(() => {
  const { selectedDeviceId, hasDevices, devicesLoading } = useCloudContext()
  
  // Initialize polling controller for query reduction
  const pollingController = usePollingController()
  
  // Track when fetch interceptor is ready
  const [interceptorReady, setInterceptorReady] = useState(false)
  
  // Track device connection status
  const [deviceNotConnected, setDeviceNotConnected] = useState(false)
  const [checkingConnection, setCheckingConnection] = useState(false)
  
  // Intelligent caching for cloud function calls
  const cacheRef = useRef(new Map())
  const lastFetchRef = useRef(new Map())
  
  // Memoized Firebase Functions - stable reference prevents re-renders
  const firebaseFunctions = useMemo(() => Object.freeze({
    getDashboardFields: httpsCallable(functions, 'getDashboardFields'),
    getRealTimeData: httpsCallable(functions, 'getRealTimeData'),
    getHistoricalData: httpsCallable(functions, 'getHistoricalData'),
    getCurrentFaults: httpsCallable(functions, 'getCurrentFaults'),
    getDashboardCombined: httpsCallable(functions, 'getDashboardCombined'),
  }), [])

  // Check if device has recent data (connection check)
  const checkDeviceConnection = useMemo(() => async () => {
    if (!selectedDeviceId) return
    
    setCheckingConnection(true)
    try {
      const result = await firebaseFunctions.getRealTimeData({ 
        serialNumber: selectedDeviceId,
      })
      
      // If no data returned, device hasn't connected yet
      const hasRecentData = result.data?.success && Array.isArray(result.data.data) && result.data.data.length > 0
      setDeviceNotConnected(!hasRecentData)
    } catch (error) {
      console.error('Device connection check failed:', error)
      setDeviceNotConnected(true)
    } finally {
      setCheckingConnection(false)
    }
  }, [selectedDeviceId, firebaseFunctions])

  // Check device connection when device changes
  useEffect(() => {
    if (selectedDeviceId && interceptorReady) {
      checkDeviceConnection()
    }
  }, [selectedDeviceId, interceptorReady, checkDeviceConnection])

  // Clear cache when device changes to avoid stale data
  useEffect(() => {
    cacheRef.current.clear()
    lastFetchRef.current.clear()
    setDeviceNotConnected(false)
    setCheckingConnection(false)
  }, [selectedDeviceId])

  // Streamlined fetch interceptor with intelligent caching and throttling
  useEffect(() => {
    if (!selectedDeviceId) {
      setInterceptorReady(false)
      return
    }
    
    const originalFetch = globalThis.fetch
    const cache = cacheRef.current
    const lastFetch = lastFetchRef.current
    
    // Cache durations for different endpoint types (in milliseconds)
    const CACHE_DURATIONS = {
      '/api/dashboard-fields': 30000,  // 30 seconds - fields don't change often
      '/api': 30000,                   // 30 seconds - real-time data
      '/faults': 30000,                // 30 seconds - faults
      '/api/historical': 60000,        // 60 seconds - historical data
    }
    
    // Helper to check if cached data is still valid
    const isCacheValid = (cacheKey, duration) => {
      const lastFetchTime = lastFetch.get(cacheKey)
      return lastFetchTime && (Date.now() - lastFetchTime) < duration
    }
    
    // Helper to get cache key for URL
    const getCacheKey = (urlString) => {
      // For historical queries, include device and measurement in cache key
      if (urlString.startsWith('/api/historical?')) {
        const params = new URLSearchParams(urlString.split('?')[1])
        return `historical-${params.get('device')}-${params.get('measurement')}`
      }
      return urlString
    }
    
    const apiRoutes = new Map([
      // Combined Dashboard Data - GET /api/dashboard-combined (50% query reduction)
      ['/api/dashboard-combined', async () => {
        const cacheKey = '/api/dashboard-combined'
        
        // Check polling controller before making calls
        if (!pollingController?.shouldPoll) {
          return { realTimeData: [], faults: [] }
        }
        
        if (isCacheValid(cacheKey, CACHE_DURATIONS['/api'])) {
          return cache.get(cacheKey)
        }
        
        const result = await firebaseFunctions.getDashboardCombined({
          serialNumber: selectedDeviceId,
        })
        
        const combinedData = {
          realTimeData: result.data?.success ? result.data.realTimeData : [],
          faults: result.data?.success ? result.data.faults : [],
        }
        
        // Update device connection status based on data availability
        const hasRecentData = Array.isArray(combinedData.realTimeData) && combinedData.realTimeData.length > 0
        setDeviceNotConnected(!hasRecentData)
        
        // Cache the result
        cache.set(cacheKey, combinedData)
        lastFetch.set(cacheKey, Date.now())
        
        return combinedData
      }],
      
      // Dashboard Fields Discovery - GET /api/dashboard-fields (cached 30s)
      ['/api/dashboard-fields', async () => {
        const cacheKey = '/api/dashboard-fields'
        
        if (isCacheValid(cacheKey, CACHE_DURATIONS['/api/dashboard-fields'])) {
          return cache.get(cacheKey)
        }
        
        const result = await firebaseFunctions.getDashboardFields({
          serialNumber: selectedDeviceId,
        })
        const fields = result.data?.success ? result.data.fields : []
        
        // Cache the result
        cache.set(cacheKey, fields)
        lastFetch.set(cacheKey, Date.now())
        
        return fields
      }],
      
      // Real-Time Data - GET /api (routed to combined endpoint for efficiency)
      // Express polls every 1s, but we only call cloud function every 30s via combined endpoint
      ['/api', async () => {
        const cacheKey = '/api'
        
        // Check polling controller before making calls
        if (!pollingController?.shouldPoll) {
          return []
        }
        
        if (isCacheValid(cacheKey, CACHE_DURATIONS['/api'])) {
          return cache.get(cacheKey)
        }
        
        // OPTIMIZATION: Use combined endpoint to get both data and faults, return only real-time data
        const result = await firebaseFunctions.getDashboardCombined({
          serialNumber: selectedDeviceId,
        })
        const data = result.data?.success ? result.data.realTimeData : []
        
        // Update device connection status based on data availability
        const hasRecentData = Array.isArray(data) && data.length > 0
        setDeviceNotConnected(!hasRecentData)
        
        // Cache both individual and combined data for efficiency
        cache.set(cacheKey, data)
        cache.set('/faults', result.data?.success ? result.data.faults : [])
        cache.set('/api/dashboard-combined', {
          realTimeData: data,
          faults: result.data?.success ? result.data.faults : [],
        })
        lastFetch.set(cacheKey, Date.now())
        lastFetch.set('/faults', Date.now())
        lastFetch.set('/api/dashboard-combined', Date.now())
        
        return data
      }],
      
      // Historical Data - GET /api/historical?device=&measurement=&start=&end= (cached 60s per device/measurement)
      ['/api/historical?', async urlString => {
        const params = new URLSearchParams(urlString.split('?')[1])
        const cacheKey = getCacheKey(urlString)
        
        if (isCacheValid(cacheKey, CACHE_DURATIONS['/api/historical'])) {
          return cache.get(cacheKey)
        }
        
        // Add fallback time window if missing or too short (less than 3 minutes)
        let start = params.get('start')
        let end = params.get('end')
        
        if (!start || !end) {
          // Default to last 3 minutes if no time range provided
          const now = new Date()
          end = now.toISOString()
          start = new Date(now.getTime() - 3 * 60 * 1000).toISOString() // 3 minutes ago
        } else {
          // Check if time window is too short (less than 3 minutes)
          const startTime = new Date(start)
          const endTime = new Date(end)
          const durationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60)
          
          if (durationMinutes < 3) {
            // Extend to 3 minutes for meaningful data
            const now = new Date()
            end = now.toISOString()
            start = new Date(now.getTime() - 3 * 60 * 1000).toISOString() // 3 minutes ago
          }
        }
        
        const result = await firebaseFunctions.getHistoricalData({
          serialNumber: selectedDeviceId,
          device: params.get('device'),
          measurement: params.get('measurement'),
          start,
          end,
        })
        const data = result.data?.success ? result.data.data : []
        
        // Cache the result
        cache.set(cacheKey, data)
        lastFetch.set(cacheKey, Date.now())
        
        return data
      }],
      
      // Current Faults - GET /faults (routed to combined endpoint for efficiency)
      ['/faults', async () => {
        const cacheKey = '/faults'
        
        // Check polling controller before making calls
        if (!pollingController?.shouldPoll) {
          return []
        }
        
        if (isCacheValid(cacheKey, CACHE_DURATIONS['/faults'])) {
          return cache.get(cacheKey)
        }
        
        // OPTIMIZATION: Use combined endpoint to get both data and faults, return only faults
        const result = await firebaseFunctions.getDashboardCombined({
          serialNumber: selectedDeviceId,
        })
        const faults = result.data?.success ? result.data.faults : []
        
        // Cache both individual and combined data for efficiency
        cache.set(cacheKey, faults)
        cache.set('/api', result.data?.success ? result.data.realTimeData : [])
        cache.set('/api/dashboard-combined', {
          realTimeData: result.data?.success ? result.data.realTimeData : [],
          faults,
        })
        lastFetch.set(cacheKey, Date.now())
        lastFetch.set('/api', Date.now())
        lastFetch.set('/api/dashboard-combined', Date.now())
        
        return faults
      }],
    ])

    globalThis.fetch = async (input, options) => {
      try {
        // Handle both URL strings and Request objects
        const urlString = typeof input === 'string' ? input : input?.url ?? ''
        
        // Only intercept our dashboard API routes, let Firebase requests pass through
        if (urlString.startsWith('/api') || urlString.startsWith('/faults')) {
          for (const [pattern, handler] of apiRoutes) {
            try {
              // Improved pattern matching
              let shouldIntercept = false
              if (pattern.endsWith('?')) {
                // Pattern like '/api/historical?' should match '/api/historical?device=...'
                shouldIntercept = urlString.startsWith(pattern.slice(0, -1))
              } else {
                // Exact match for patterns like '/faults', '/api', '/api/dashboard-fields'
                shouldIntercept = urlString === pattern
              }
              
              if (shouldIntercept) {
                const data = await handler(urlString)
                return {
                  ok: true,
                  status: 200,
                  statusText: 'OK',
                  headers: new Headers({ 'content-type': 'application/json' }),
                  json: async () => data,
                  text: async () => JSON.stringify(data),
                }
              }
            } catch (error) {
              console.error('❌ Error in dashboard API handler:', error)
              return {
                ok: false,
                status: 500,
                statusText: 'Internal Server Error',
                headers: new Headers({ 'content-type': 'application/json' }),
                json: async () => ({ error: error.message }),
                text: async () => JSON.stringify({ error: error.message }),
              }
            }
          }
          
          // If no pattern matched, return 404
          console.warn('⚠️ No pattern matched for:', urlString)
          return {
            ok: false,
            status: 404,
            statusText: 'Not Found',
            headers: new Headers({ 'content-type': 'application/json' }),
            json: async () => ({ error: 'API endpoint not found in dashboard interceptor' }),
            text: async () => JSON.stringify({ error: 'API endpoint not found in dashboard interceptor' }),
          }
        }
        
        return originalFetch(input, options)
      } catch (error) {
        console.error('Dashboard API proxy error:', error)
        return {
          ok: false,
          status: 500,
          json: async () => ({ error: error.message }),
        }
      }
    }
    
    // Mark interceptor as ready and setup cleanup
    setInterceptorReady(true)
    
    return () => { 
      // Create a graceful handler that will persist until components stop making calls
      let callCount = 0
      const gracefulHandler = async (input, options) => {
        const urlString = typeof input === 'string' ? input : input?.url ?? ''
        
        // Handle dashboard API calls with empty responses to prevent 404s
        if (urlString.startsWith('/api') || urlString.startsWith('/faults')) {
          callCount++
          
          // After many calls with no activity, restore original fetch
          if (callCount > 50) { // After ~50 API calls (should be enough for cleanup)
            globalThis.fetch = originalFetch
            return originalFetch(input, options)
          }
          
          return {
            ok: true,
            status: 200,
            statusText: 'OK',
            headers: new Headers({ 'content-type': 'application/json' }),
            json: async () => [],
            text: async () => '[]',
          }
        }
        
        // For all other requests, use original fetch
        return originalFetch(input, options)
      }
      
      // Set graceful handler immediately
      globalThis.fetch = gracefulHandler
      setInterceptorReady(false)
      
      // Clear cache
      cache.clear()
      lastFetch.clear()
      
      // Also set a backup timeout as a safety net
      setTimeout(() => {
        if (globalThis.fetch === gracefulHandler) {
          globalThis.fetch = originalFetch
        }
      }, 10000) // 10 second backup timeout
    }
  }, [selectedDeviceId, firebaseFunctions, pollingController?.shouldPoll])

  // Early returns with consistent loading states
  if (devicesLoading) return <LoadingSpinner message="Loading device data..." />
  
  if (!hasDevices) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-speedometer2" style={{ fontSize: '4rem', color: 'var(--text-light-muted)' }} />
        <h4 className="mt-3 mb-3">No Devices to Monitor</h4>
        <p className="text-muted">Add a device to start monitoring real-time performance data.</p>
      </div>
    )
  }
  
  if (!selectedDeviceId) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2" />
        Please select a device to view its dashboard data.
      </div>
    )
  }
  
  // Wait for fetch interceptor to be ready before rendering dashboard
  if (!interceptorReady) {
    return <LoadingSpinner message="Setting up dashboard..." />
  }
  
  // Show connection checking state
  if (checkingConnection) {
    return <LoadingSpinner message="Checking device connection..." />
  }
  
  // Show device not connected state when device exists but has no recent data
  if (deviceNotConnected) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-wifi-off" style={{ fontSize: '4rem', color: 'var(--text-light-muted)' }} />
        <h4 className="mt-3 mb-3">Device Not Connected</h4>
        <p className="text-muted mb-4">
          The device <strong>{selectedDeviceId}</strong> hasn&apos;t sent any recent data to the cloud.
        </p>
        <div className="text-muted">
          <p className="mb-2"><strong>Troubleshooting:</strong></p>
          <ul className="list-unstyled">
            <li>• Check that the device is powered on</li>
            <li>• Verify internet/cellular connectivity</li>
            <li>• Ensure the device is properly configured</li>
          </ul>
        </div>
        <button 
          className="btn btn-outline-primary mt-3"
          onClick={() => {
            checkDeviceConnection()
          }}
        >
          <i className="bi bi-arrow-clockwise me-2" />
          Check Connection Again
        </button>
      </div>
    )
  }
  
  return (
    <>
      <PresenceConfirmation 
        isVisible={pollingController?.needsPresenceConfirmation || false}
        onConfirm={pollingController?.confirmPresence}
        onPause={pollingController?.pauseUpdates}
      />
      <DashboardPage />
    </>
  )
})

Dashboard.displayName = 'Dashboard'

export default Dashboard
